import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useMutation, useQuery } from '@tanstack/react-query';
import Breadcrumbs from 'src/components/shared/breadcrumbs/breadcrumbs';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { ASSOCIATE_PROFILE_BASEURL, PLACEMENT_DETAILS_BASEURL } from 'src/constants/Routes';
import {
  getAccommodationMutationOptions,
  getAccommodationQueryOptions,
} from 'src/services/accommodation/accommodationQueries';
import { getSiteQueryOptions } from 'src/services/sites/siteQueries';
import { clearProcessingMessagesData } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { constructIncidentLink } from 'src/utils/constructRTWLinks';
import { stripOutLeadingHash } from 'src/utils/helpers';

import TopDetails from './topDetails';
import TopOptions from './topOptions';
import TopTabs from './topTabs';

import SendEmailModal from './sendEmailModal/sendEmailModal';
import Correspondence from './subpages/correspondence/correspondence';
import EmployeeInformation from './subpages/employeeInformation/employeeInformation';
import PlacementSelections from './subpages/placementSelections/placementSelections';
import Restrictions from './subpages/restrictions/restrictions';
import RevisionHistory from './subpages/revisionHistory/revisionHistory';

/**
 * Default component.
 */
const PlacementDetails = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { placementId } = useParams();
  const { featureFlags } = useAppSelector((state) => state.user);
  const { accommodationType, processingMessages, externalCaseId } = useAppSelector((state) => state.newJmrPage);
  const [selectedTab, setSelectedTab] = useState('placement-selections');
  const { data: allowedSites, ...allowedSitesQuery } = useQuery(getSiteQueryOptions.allowedSites());
  const { data: placementDetails, ...placementQuery } = useQuery(
    getAccommodationQueryOptions.accommodationDetails(+placementId!),
  );
  const deletePlacement = useMutation(getAccommodationMutationOptions.delete());
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const associateIdentifier = placementDetails?.associateFullName ?? placementDetails?.login;
  const associateProfileCrumb = featureFlags.PERSONA
    ? [{ label: `Associate (${associateIdentifier})`, url: `${ASSOCIATE_PROFILE_BASEURL}/${placementDetails?.login}` }]
    : [];

  const breadcrumbs = [
    { label: `Placements Overview ${placementDetails?.fc}`, url: '/placementsOverview' },
    ...associateProfileCrumb,
    {
      label: featureFlags.PERSONA ? 'Placement Details' : `Placement Details (${associateIdentifier})`,
      url: `${PLACEMENT_DETAILS_BASEURL}/${placementId}`,
    },
  ];

  // Initalize "dispatchToast()"" function
  const dispatchToast = (type: 'error' | 'success', message: string, incidentLink?: string | null) =>
    dispatch(
      createToast({
        type: type,
        message: (
          <>
            <div>{message}</div>
            {incidentLink && (
              <div>
                <Link target="_blank" rel="noopener noreferrer" type="secondary" href={incidentLink}>
                  {incidentLink}
                </Link>
              </div>
            )}
          </>
        ),
        timeout: TOAST_TIMEOUT,
      }),
    );

  // Show toast when messages are done processing
  useEffect(() => {
    if (processingMessages && accommodationType === 'AUSTIN') {
      const incidentLink = externalCaseId && constructIncidentLink(stripOutLeadingHash(externalCaseId));
      processingMessages.forEach((message) => {
        if (message.type === 'ERROR') {
          if (message.content.includes('AUSTIN')) {
            dispatchToast('error', message.content, incidentLink);
          } else {
            dispatchToast('error', message.content);
          }
        } else if (message.type === 'SUCCESS') {
          dispatchToast('success', message.content);
        }
      });
      dispatch(clearProcessingMessagesData());
    }
  }, [processingMessages]);

  // Delete a placement
  const removePlacement = async () => {
    if (placementId) {
      await deletePlacement.mutateAsync(+placementId);
      setIsConfirmDeleteModalOpen(false);
      navigate('/placementsOverview');
    }
  };

  // When the page unloads
  useEffect(() => {
    return () => {
      // Reset the tab to placement-selections when leaving this component so that the
      // next time you enter this component, it defaults to placement-selections
      setSelectedTab('placement-selections');
    };
  }, []);

  if (placementQuery.isError || allowedSitesQuery.isError) {
    return <Text>Oops! Something went wrong.</Text>;
  }

  return (
    <LoadingSpinnerWrapper showSpinner={placementQuery.isLoading || allowedSitesQuery.isLoading}>
      {placementId && (
        <Column spacingInset="400 500" spacing="500">
          {/* Breadcrumbs */}
          <Breadcrumbs breadcrumbList={breadcrumbs} />

          {/* Main Body */}
          <Column spacing="450">
            <Column spacing="400">
              <TopOptions setIsDeleteModalOpen={setIsConfirmDeleteModalOpen} />
              <TopDetails />
            </Column>
            <TopTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />

            <SendEmailModal />
            <Row spacingInset="400 none 600 none" widths={['fill']}>
              {selectedTab === 'placement-selections' && <PlacementSelections />}
              {selectedTab === 'restrictions' && <Restrictions />}
              {selectedTab === 'employee-information' && <EmployeeInformation />}
              {selectedTab === 'revision-history' && <RevisionHistory />}
              {selectedTab === 'correspondence' && <Correspondence />}
            </Row>
          </Column>
        </Column>
      )}

      {/* Modals */}
      <ConfirmModal
        modalTitle="Confirm delete?"
        modalDescription="This will permanently delete this placement"
        open={isConfirmDeleteModalOpen}
        showSpinner={deletePlacement.isPending}
        setOpen={(open) => setIsConfirmDeleteModalOpen(open)}
        onConfirm={() => removePlacement()}
      />
    </LoadingSpinnerWrapper>
  );
};

export default PlacementDetails;
